import { useState, useEffect } from "react";
import axios from "axios";
import { MapPin, Phone, Mail, Clock, Facebook, Instagram } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContattiPage = () => {
  const [contactInfo, setContactInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchContact = async () => {
      try {
        const response = await axios.get(`${API}/contact`);
        setContactInfo(response.data);
      } catch (error) {
        console.error("Error fetching contact info:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchContact();
  }, []);

  return (
    <div data-testid="contatti-page">
      <Navbar />

      {/* Hero */}
      <section className="pt-24 lg:pt-32 pb-12 bg-[#0047AB] noise-overlay">
        <div className="max-w-7xl mx-auto px-4 text-center text-white">
          <h1 className="text-4xl lg:text-5xl font-black uppercase mb-4 animate-fade-in-up">
            Contatti
          </h1>
          <p className="text-blue-200 max-w-xl mx-auto">
            Vieni a trovarci o contattaci per qualsiasi informazione
          </p>
        </div>
      </section>

      <section className="section-padding bg-slate-50">
        <div className="max-w-4xl mx-auto px-4">
          {/* Contact Info */}
          <div data-testid="contact-info-section">
            <h2 className="text-2xl font-bold text-slate-900 mb-8 text-center">
              Dove Siamo
            </h2>

            {loading ? (
              <div className="animate-pulse space-y-6">
                <div className="h-24 bg-slate-200 rounded-xl" />
                <div className="h-24 bg-slate-200 rounded-xl" />
                <div className="h-24 bg-slate-200 rounded-xl" />
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-6">
                {/* Address */}
                <div className="flex gap-4 p-6 bg-white rounded-xl shadow-sm">
                  <div className="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-[#0047AB]" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900">Indirizzo</h3>
                    <p className="text-slate-600">
                      {contactInfo?.field_name}
                    </p>
                    <p className="text-slate-500 text-sm">
                      {contactInfo?.address}
                    </p>
                  </div>
                </div>

                {/* Phone */}
                <div className="flex gap-4 p-6 bg-white rounded-xl shadow-sm">
                  <div className="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-[#0047AB]" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900">Telefono</h3>
                    <a
                      href={`tel:${contactInfo?.phone}`}
                      className="text-[#0047AB] hover:underline"
                    >
                      {contactInfo?.phone}
                    </a>
                  </div>
                </div>

                {/* Email */}
                <div className="flex gap-4 p-6 bg-white rounded-xl shadow-sm">
                  <div className="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-[#0047AB]" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900">Email</h3>
                    <a
                      href={`mailto:${contactInfo?.email}`}
                      className="text-[#0047AB] hover:underline"
                    >
                      {contactInfo?.email}
                    </a>
                  </div>
                </div>

                {/* Hours */}
                <div className="flex gap-4 p-6 bg-white rounded-xl shadow-sm">
                  <div className="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-[#0047AB]" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900">
                      Orari Segreteria
                    </h3>
                    <p className="text-slate-600">Lun - Ven: 17:00 - 19:00</p>
                    <p className="text-slate-500 text-sm">
                      Sabato: 9:00 - 12:00
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Social Links */}
            <div className="mt-8 bg-white rounded-xl shadow-sm p-6">
              <h3 className="font-bold text-slate-900 mb-4 text-center">Seguici sui Social</h3>
              <div className="flex justify-center gap-4">
                <a
                  href="https://www.facebook.com/gsdvolpiano/?locale=it_IT"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-6 py-3 bg-[#1877F2] text-white rounded-full hover:bg-[#1565c0] transition-colors"
                >
                  <Facebook className="w-5 h-5" />
                  <span className="font-medium">Facebook</span>
                </a>
                <a
                  href="https://www.instagram.com/gsdvolpianopianese/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#833AB4] via-[#FD1D1D] to-[#F77737] text-white rounded-full hover:opacity-90 transition-opacity"
                >
                  <Instagram className="w-5 h-5" />
                  <span className="font-medium">Instagram</span>
                </a>
              </div>
            </div>

            {/* Map */}
            <div className="mt-8 rounded-xl overflow-hidden shadow-lg h-80">
              <iframe
                data-testid="contact-map"
                title="Mappa Stadio Volpiano"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2827.5!2d7.7742!3d45.2008!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x478872d0dc4ae9f7%3A0x2c0c7c7db3b5e5a!2sVia%20Trento%2C%20104%2C%2010088%20Volpiano%20TO!5e0!3m2!1sit!2sit!4v1234567890"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContattiPage;
